import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import cn from 'classnames';
import styles from './Breadcrumbs.module.sass';

/**
 * Composant Breadcrumbs pour la navigation et le SEO
 * @param {Object} props - Props du composant
 * @param {Array} props.items - Tableau des éléments du breadcrumb
 * @param {string} props.className - Classes CSS additionnelles
 */
const Breadcrumbs = ({ items = [], className }) => {
  const location = useLocation();

  // Génération automatique des breadcrumbs si pas d'items fournis
  const generateBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter(x => x);
    const breadcrumbs = [{ name: 'Accueil', path: '/' }];

    pathnames.forEach((pathname, index) => {
      const path = `/${pathnames.slice(0, index + 1).join('/')}`;
      
      // Mapping des chemins vers des noms lisibles
      const nameMapping = {
        'actualites': 'Actualités',
        'article': 'Article',
        'forfaits': 'Forfaits',
        'contact': 'Contact',
        'opco': 'OPCO',
        'contrat-apprentissage': 'Contrat d\'apprentissage',
        'emargement-assiduite': 'Émargement & Assiduité',
        'prospection-leads': 'Prospection & Leads',
        'rapports-statistiques': 'Rapports & Statistiques',
        'gestion-documents': 'Gestion de documents',
        'cgu': 'Conditions d\'utilisation',
        'confidentialite': 'Politique de confidentialité'
      };

      breadcrumbs.push({
        name: nameMapping[pathname] || pathname.charAt(0).toUpperCase() + pathname.slice(1),
        path: path
      });
    });

    return breadcrumbs;
  };

  const breadcrumbItems = items.length > 0 ? items : generateBreadcrumbs();

  if (breadcrumbItems.length <= 1) {
    return null; // Ne pas afficher si seulement l'accueil
  }

  // Structured data pour les breadcrumbs
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://edutio.fr${item.path}`
    }))
  };

  return (
    <>
      {/* Structured data pour Google */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <nav className={cn(styles.breadcrumbs, className)} aria-label="Breadcrumb">
        <ol className={styles.list}>
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;
            
            return (
              <li key={index} className={styles.item}>
                {isLast ? (
                  <span className={styles.current} aria-current="page">
                    {item.name}
                  </span>
                ) : (
                  <>
                    <Link 
                      to={item.path} 
                      className={styles.link}
                      title={`Aller à ${item.name}`}
                    >
                      {item.name}
                    </Link>
                    <span className={styles.separator} aria-hidden="true">
                      /
                    </span>
                  </>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};

export default Breadcrumbs;
